import { useMemo, useState } from "react";
import { ArrowRight, ArrowLeft, Check, Sparkles, MapPin, FileText, Clock } from "lucide-react";
import { COMPANY } from "../../lib/constants";
import { WhatsAppIcon } from "./Header";
import { LegalModal } from "../LegalModal";
import { useInView, revealClass } from "../../hooks/useInView";

const TYPES = ["Wohnung", "Haus", "Keller", "Garage", "Dachboden", "Schrott", "Messie", "Gewerbe"];
const SIZES = ["Kleines Volumen", "Bis 30 m²", "30–70 m²", "70–120 m²", "Über 120 m²"];
const WHEN = ["So schnell wie möglich", "Diese Woche", "Nächste Woche", "Flexibel"];

const STEPS = [
  { n: 1, label: "Objekt", icon: FileText },
  { n: 2, label: "Umfang & Zeit", icon: Clock },
  { n: 3, label: "Kontakt", icon: MapPin },
];

const buildMessage = ({ type, size, when, address, name, note }) => {
  const lines = [
    "Hallo BRS, ich möchte ein kostenloses Festpreis-Angebot.",
    "",
    `• Objekt: ${type || "—"}`,
    `• Umfang: ${size || "—"}`,
    `• Zeitraum: ${when || "—"}`,
  ];
  if (address) lines.push(`• Adresse / PLZ: ${address}`);
  if (name) lines.push(`• Name: ${name}`);
  if (note) {
    lines.push("");
    lines.push(`Hinweis: ${note}`);
  }
  lines.push("");
  lines.push("Bitte melden Sie sich. Danke!");
  return lines.join("\n");
};

export const Quote = () => {
  const [headRef, headIn] = useInView();
  const [step, setStep] = useState(1);
  const [type, setType] = useState("");
  const [size, setSize] = useState("");
  const [when, setWhen] = useState("");
  const [address, setAddress] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [consent, setConsent] = useState(false);
  const [dsgvoOpen, setDsgvoOpen] = useState(false);

  const canNext = (step === 1 && type) || (step === 2 && size);
  const isFinal = step === 3;
  const canSubmit = isFinal && consent;

  const waUrl = useMemo(() => {
    const text = buildMessage({ type, size, when, address, name, note });
    return `https://wa.me/${COMPANY.whatsappNumber}?text=${encodeURIComponent(text)}`;
  }, [type, size, when, address, name, note]);

  const handleNext = () => {
    if (isFinal) {
      if (!consent) return;
      window.open(waUrl, "_blank", "noopener,noreferrer");
      return;
    }
    if (!canNext) return;
    setStep((s) => Math.min(3, s + 1));
  };

  const progress = Math.round((step / 3) * 100);

  return (
    <section
      id="angebot"
      data-testid="quote-section"
      className="relative py-14 md:py-24 bg-white overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-100 pointer-events-none bg-recycling-dense"
        aria-hidden
      />

      <div className="relative z-10 px-4 md:px-8 lg:px-12 max-w-[1100px] mx-auto">
        {/* Header */}
        <div
          ref={headRef}
          {...revealClass(headIn)}
          className="text-center mb-8 md:mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-black text-[#25D366] font-mono text-[10px] md:text-[11px] tracking-[0.3em] uppercase px-3 py-1.5 rounded-full">
            <Sparkles className="w-3.5 h-3.5" />
            Kostenlos &amp; unverbindlich
          </div>
          <h2 className="mt-5 font-display text-[40px] sm:text-5xl lg:text-[80px] font-semibold tracking-[-0.045em] text-black leading-[0.95]">
            In 60 Sekunden <br className="hidden sm:block" />
            <span className="text-black/40">zum Festpreis.</span>
          </h2>
          <p className="mt-4 text-black/60 text-sm md:text-base max-w-md mx-auto">
            3 Schritte → ab in WhatsApp → wir melden uns mit Festpreis-Angebot.
          </p>
        </div>

        {/* WIZARD CARD */}
        <div
          data-testid="quote-form"
          className="relative rounded-[28px] border-2 border-black bg-white shadow-[8px_8px_0_rgba(0,0,0,1)] md:shadow-[12px_12px_0_rgba(0,0,0,1)] overflow-hidden"
        >
          {/* Steps header */}
          <div className="px-5 md:px-8 pt-6 md:pt-7 pb-4 border-b border-black/10">
            <div className="flex items-center justify-between mb-4">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                const active = step === s.n;
                const done = step > s.n;
                return (
                  <div key={s.n} className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
                    <div
                      className={`relative shrink-0 w-9 h-9 md:w-10 md:h-10 rounded-full border-2 flex items-center justify-center transition-all ${
                        active
                          ? "bg-[#25D366] border-black"
                          : done
                          ? "bg-black border-black text-white"
                          : "bg-white border-black/15 text-black/40"
                      }`}
                    >
                      {done ? (
                        <Check className="w-4 h-4" strokeWidth={3} />
                      ) : (
                        <Icon className={`w-4 h-4 ${active ? "text-black" : ""}`} />
                      )}
                    </div>
                    <div className="hidden sm:block min-w-0">
                      <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-black/40">
                        Schritt {s.n}
                      </div>
                      <div
                        className={`text-xs md:text-sm font-medium truncate ${
                          active || done ? "text-black" : "text-black/40"
                        }`}
                      >
                        {s.label}
                      </div>
                    </div>
                    {i < STEPS.length - 1 && (
                      <div
                        className={`flex-1 h-px ml-2 md:ml-3 transition-colors ${
                          done ? "bg-black" : "bg-black/10"
                        }`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            {/* Progress bar */}
            <div className="h-1.5 bg-black/5 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#25D366] rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Step content */}
          <div className="px-5 md:px-8 py-6 md:py-8 min-h-[340px] md:min-h-[360px]">
            {step === 1 && (
              <div className="step-in">
                <Label num="01">Was soll entrümpelt werden?</Label>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 md:gap-3">
                  {TYPES.map((t) => (
                    <BigChip
                      key={t}
                      active={type === t}
                      onClick={() => setType(t)}
                      testid={`quote-type-${t}`}
                    >
                      {t}
                    </BigChip>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="step-in space-y-7">
                <div>
                  <Label num="02">Wie viel ungefähr?</Label>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {SIZES.map((s) => (
                      <BigChip
                        key={s}
                        active={size === s}
                        onClick={() => setSize(s)}
                        testid={`quote-size-${s}`}
                      >
                        {s}
                      </BigChip>
                    ))}
                  </div>
                </div>
                <div>
                  <Label num="03" optional>Wann?</Label>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {WHEN.map((w) => (
                      <BigChip
                        key={w}
                        active={when === w}
                        onClick={() => setWhen(w)}
                        testid={`quote-when-${w}`}
                      >
                        {w}
                      </BigChip>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="step-in space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input
                    label="PLZ / Stadtteil"
                    optional
                    value={address}
                    onChange={setAddress}
                    placeholder="z.B. 28195 / Walle"
                    testid="quote-address"
                  />
                  <Input
                    label="Name"
                    optional
                    value={name}
                    onChange={setName}
                    placeholder="Vor- und Nachname"
                    testid="quote-name"
                  />
                </div>
                <Input
                  label="Kurze Beschreibung"
                  optional
                  value={note}
                  onChange={setNote}
                  placeholder="z.B. 2 alte Sofas, Kühlschrank, Gartenmüll …"
                  multiline
                  testid="quote-note"
                />

                {/* Summary */}
                <div className="rounded-2xl bg-black text-white p-4 md:p-5">
                  <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-white/55 mb-2">
                    Zusammenfassung
                  </div>
                  <div className="grid sm:grid-cols-3 gap-2 text-xs md:text-sm">
                    <Sum k="Objekt" v={type || "—"} />
                    <Sum k="Umfang" v={size || "—"} />
                    <Sum k="Wann" v={when || "—"} />
                  </div>
                </div>

                {/* DSGVO consent */}
                <label
                  data-testid="quote-dsgvo-checkbox"
                  className={`flex items-start gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                    consent
                      ? "border-black bg-black/[0.03]"
                      : "border-black/15 hover:border-black/40"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    data-testid="quote-dsgvo-input"
                    className="sr-only"
                  />
                  <span
                    className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                      consent
                        ? "bg-[#25D366] border-black"
                        : "bg-white border-black/30"
                    }`}
                  >
                    {consent && <Check className="w-3.5 h-3.5 text-black" strokeWidth={3} />}
                  </span>
                  <span className="text-xs md:text-sm text-black/75 leading-relaxed">
                    Ich habe die{" "}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setDsgvoOpen(true);
                      }}
                      data-testid="quote-dsgvo-link"
                      className="underline underline-offset-2 decoration-black/40 hover:decoration-black font-semibold text-black"
                    >
                      Datenschutzerklärung
                    </button>{" "}
                    gelesen und stimme der Verarbeitung meiner Daten (DSGVO) zur
                    Bearbeitung meiner Anfrage zu.
                  </span>
                </label>
              </div>
            )}
          </div>

          {/* Footer / Navigation */}
          <div className="px-5 md:px-8 pb-6 md:pb-7 pt-2 flex flex-col-reverse sm:flex-row gap-3 sm:items-center sm:justify-between border-t border-black/10 bg-black/[0.02]">
            <button
              type="button"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              data-testid="quote-back"
              className={`inline-flex items-center justify-center gap-2 h-12 px-5 rounded-full text-sm font-medium transition-colors ${
                step === 1
                  ? "text-black/30 cursor-not-allowed"
                  : "text-black hover:bg-black hover:text-white"
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück
            </button>

            <button
              type="button"
              onClick={handleNext}
              disabled={isFinal ? !canSubmit : !canNext}
              data-testid={isFinal ? "quote-submit" : "quote-next"}
              className={`group inline-flex items-center justify-center gap-2 md:gap-3 h-14 w-full sm:w-auto px-6 md:px-8 rounded-full text-sm md:text-base font-semibold transition-all ${
                (isFinal ? canSubmit : canNext)
                  ? isFinal
                    ? "bg-[#25D366] text-black border-2 border-black shadow-[5px_5px_0_rgba(0,0,0,1)] hover:shadow-[2px_2px_0_rgba(0,0,0,1)] hover:translate-x-[3px] hover:translate-y-[3px] glow-pulse"
                    : "bg-black text-white hover:bg-black/85"
                  : "bg-black/[0.06] text-black/35 cursor-not-allowed"
              }`}
            >
              {isFinal && <WhatsAppIcon className="w-4 h-4 md:w-5 md:h-5" />}
              {isFinal ? "Per WhatsApp senden" : "Weiter"}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>
        </div>

        {/* Trust line below */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] md:text-xs text-black/55">
          {["0 € Anfahrt", "100 % Festpreis", "Keine Vorauszahlung", "< 24 h Antwort"].map((t) => (
            <span key={t} className="flex items-center gap-1.5">
              <span className="w-4 h-4 rounded-full bg-[#25D366] text-black flex items-center justify-center">
                <Check className="w-2.5 h-2.5" strokeWidth={3} />
              </span>
              {t}
            </span>
          ))}
        </div>
      </div>

      <LegalModal
        open={dsgvoOpen}
        onClose={() => setDsgvoOpen(false)}
        type="datenschutz"
      />
    </section>
  );
};

const Label = ({ num, children, optional }) => (
  <div>
    <div className="font-mono text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-black/55 flex items-center gap-2">
      <span>/ {num}</span>
      {optional && <span className="text-black/35">optional</span>}
    </div>
    <h3 className="mt-2 font-display text-xl md:text-2xl font-semibold tracking-tight text-black">
      {children}
    </h3>
  </div>
);

const BigChip = ({ children, active, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`text-sm h-12 px-4 rounded-2xl border-2 font-medium transition-all ${
      active
        ? "bg-black text-white border-black shadow-[3px_3px_0_#25D366]"
        : "bg-white text-black border-black/15 hover:border-black hover:bg-black/[0.03]"
    }`}
  >
    {children}
  </button>
);

const Input = ({ label, value, onChange, placeholder, multiline, testid, optional }) => (
  <div>
    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-black/55 mb-2 flex items-center gap-2">
      {label}
      {optional && <span className="text-black/35">optional</span>}
    </div>
    {multiline ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        data-testid={testid}
        className="w-full px-4 py-3 rounded-2xl border-2 border-black/15 bg-white text-sm text-black placeholder:text-black/35 focus:outline-none focus:border-black resize-none transition-colors"
      />
    ) : (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full px-4 h-12 rounded-full border-2 border-black/15 bg-white text-sm text-black placeholder:text-black/35 focus:outline-none focus:border-black transition-colors"
      />
    )}
  </div>
);

const Sum = ({ k, v }) => (
  <div className="flex flex-col gap-0.5">
    <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-white/45">{k}</span>
    <span className="font-medium truncate">{v}</span>
  </div>
);
